import Vue from 'vue'
import { db, FieldValue } from '@/utils/Firebase'
import { randomString, safeLower, toDate } from '@/utils/Common'
import { UserIndex } from '@/utils/Algolia'
import i18n from '@/plugins/i18n'
import store from '@/store'

export const CASH_COLLECTION = 'businessCash'

export const SETTINGS_COLLECTION = 'businessSettings'

export const SHIFTS_COLLECTION = 'businessShifts'

export const BOOKINGS_COLLECTION = 'businessShiftBookings'

export const CLIENTS_COLLECTION = 'businessClients'

export const PAYMENTS_COLLECTION = 'businessPayments'

export const PAYMENT_DUE_DAYS = 5

export const ACTIVITY_PALETTE = [
    '#66CCCC', '#66CC99', '#339999', '#9B6DFF', '#FF8C42', '#2A8080', '#E6AC00', '#4FA8D8',
]

export const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 7]

export const DAY_START_HOUR = 6

export const DAY_END_HOUR = 22

export const SLOT_MINUTES = 15

export const toMinutes = (time) => {
    const [hour, minute] = String(time || '').split(':').map(Number)
    return (Number(hour) || 0) * 60 + (Number(minute) || 0)
}

export const toTime = (minutes) => {
    const value = ((Number(minutes) || 0) + 1440) % 1440
    return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`
}

export const TIME_SLOTS = (() => {
    const slots = []
    for (let minutes = DAY_START_HOUR * 60; minutes <= DAY_END_HOUR * 60; minutes += SLOT_MINUTES) {
        slots.push(toTime(minutes))
    }
    return slots
})()

export const ADS_CONCEPT_KEY = 'ads'

export const INCOME_COLORS = ['#66CC99', '#66CCCC', '#CCFF33', '#9B6DFF', '#E6AC00', '#FF8A65']

export const EXPENSE_COLORS = ['#E85C5C', '#E6AC00', '#9B6DFF', '#66CCCC', '#FF8A65', '#66CC99']

export const cashDocId = (companyId, period) => `${companyId}_${period}`

export const bookingDocId = (shiftId, weekStart) => `${shiftId}_${weekStart}`

export const newId = () => randomString(10)

export const CURRENCIES = [
    { code: 'ARS', locale: 'es-AR', symbol: '$' },
    { code: 'BRL', locale: 'pt-BR', symbol: 'R$' },
    { code: 'CLP', locale: 'es-CL', symbol: '$' },
    { code: 'COP', locale: 'es-CO', symbol: '$' },
    { code: 'EUR', locale: 'es-ES', symbol: '€' },
    { code: 'MXN', locale: 'es-MX', symbol: '$' },
    { code: 'PEN', locale: 'es-PE', symbol: 'S/' },
    { code: 'USD', locale: 'en-US', symbol: '$' },
    { code: 'UYU', locale: 'es-UY', symbol: '$' },
]

export const DEFAULT_CURRENCY = 'ARS'

const currencyState = Vue.observable({ code: DEFAULT_CURRENCY })

let moneyFormatter = { code: null, instance: null }

export const setCurrency = (code) => {
    currencyState.code = CURRENCIES.some(item => item.code === code) ? code : DEFAULT_CURRENCY
}

export const currentCurrency = () => currencyState.code

const formatter = () => {
    const code = currencyState.code
    if (moneyFormatter.code === code) return moneyFormatter.instance
    const { locale } = CURRENCIES.find(item => item.code === code)
    const options = { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 }
    let instance
    try {
        instance = new Intl.NumberFormat(locale, { ...options, currencyDisplay: 'narrowSymbol' })
    } catch (error) {
        instance = new Intl.NumberFormat(locale, options)
    }
    moneyFormatter = { code, instance }
    return instance
}

export const formatMoney = (value) => formatter().format(Math.round(Number(value) || 0))

export const parseAmount = (value) => {
    const number = Number(String(value ?? '').replace(/[^\d.-]/g, ''))
    return Number.isFinite(number) ? number : 0
}

export const previousPeriod = (period) => {
    const year = Number(String(period).slice(0, 4))
    const month = Number(String(period).slice(5, 7))
    if (month > 1) return `${year}-${String(month - 1).padStart(2, '0')}`
    return `${year - 1}-12`
}

export const defaultFunnel = () => ({ inquiries: null, trials: null, closes: null })

const funnelValue = (value) => {
    if (value === null || value === undefined || value === '') return null
    const number = Number(value)
    if (!Number.isFinite(number) || number < 0) return null
    return Math.round(number)
}

export const normalizeFunnel = (data) => ({
    inquiries: funnelValue(data?.inquiries),
    trials: funnelValue(data?.trials),
    closes: funnelValue(data?.closes),
})

export const computeRate = (from, to) => {
    if (!from || from <= 0 || to === null || to === undefined) return null
    return Math.round(to / from * 100)
}

export const computeCac = (ads, closes) => {
    if (!closes || closes <= 0) return null
    return Math.round(ads / closes)
}

export const defaultCash = () => ({
    expenses: [
        { id: newId(), name: i18n.t('business.default_area_fixed'), items: [] },
        { id: newId(), name: i18n.t('business.default_area_variable'), items: [] },
        {
            id: newId(),
            name: i18n.t('business.default_area_marketing'),
            items: [{ id: newId(), key: ADS_CONCEPT_KEY, name: i18n.t('business.ads_investment'), amount: 0 }],
        },
        { id: newId(), name: i18n.t('business.default_area_investment'), items: [] },
    ],
    incomes: [],
    funnel: defaultFunnel(),
})

export const normalizeCash = (data) => ({
    expenses: (data?.expenses || []).map(area => ({
        id: area.id || newId(),
        name: area.name || '',
        items: (area.items || []).map(item => ({
            id: item.id || newId(),
            key: item.key || null,
            name: item.name || '',
            amount: Number(item.amount) || 0,
        })),
    })),
    incomes: (data?.incomes || []).map(item => ({
        id: item.id || newId(),
        name: item.name || '',
        amount: Number(item.amount) || 0,
    })),
    funnel: normalizeFunnel(data?.funnel),
})

export const cloneCash = (cash) => ({
    expenses: cash.expenses.map(area => ({
        ...area,
        id: newId(),
        items: area.items.map(item => ({ ...item, id: newId(), amount: 0 })),
    })),
    incomes: cash.incomes.map(item => ({ ...item, id: newId(), amount: 0 })),
    funnel: defaultFunnel(),
})

export const sumExpenses = (cash) => (cash?.expenses || [])
    .reduce((total, area) => total + sumArea(area), 0)

export const sumArea = (area) => (area?.items || [])
    .reduce((total, item) => total + (Number(item.amount) || 0), 0)

export const sumIncomes = (cash, autoIncome = 0) => (cash?.incomes || [])
    .reduce((total, item) => total + (Number(item.amount) || 0), autoIncome)

export const getAdsInvestment = (cash) => {
    for (const area of cash?.expenses || []) {
        const found = (area.items || []).find(item => item.key === ADS_CONCEPT_KEY)
        if (found) return Number(found.amount) || 0
    }
    return 0
}

export const loadCash = async (companyId, period) => {
    const doc = await db.collection(CASH_COLLECTION).doc(cashDocId(companyId, period)).get()
    if (!doc.exists) return null
    return normalizeCash(doc.data())
}

const yearCache = {}

const YEAR_CACHE_TTL = 5 * 60 * 1000

export const loadCashYear = async (companyId, year) => {
    const cacheKey = `${companyId}_${year}`
    const cached = yearCache[cacheKey]
    if (cached && (Date.now() - cached.at) < YEAR_CACHE_TTL) return cached.months

    const snapshot = await db.collection(CASH_COLLECTION)
        .where('companyId', '==', companyId)
        .where('period', '>=', `${year}-01`)
        .where('period', '<=', `${year}-12`)
        .get()

    const months = {}
    snapshot.forEach(doc => {
        const data = doc.data() || {}
        if (!data.period) return
        months[data.period] = normalizeCash(data)
    })

    yearCache[cacheKey] = { at: Date.now(), months }
    return months
}

const settingsCache = {}

const SETTINGS_TTL = 5 * 60 * 1000

export const invalidateSettings = (companyId) => {
    delete settingsCache[companyId]
}

export const loadSettings = async (companyId) => {
    const cached = settingsCache[companyId]
    if (cached && (Date.now() - cached.at) < SETTINGS_TTL) {
        setCurrency(cached.data.currency)
        return cached.data
    }

    const doc = await db.collection(SETTINGS_COLLECTION).doc(companyId).get()
    const data = doc.data() || {}
    const settings = {
        currency: data.currency || DEFAULT_CURRENCY,
        activities: (data.activities || []).map(activity => ({
            id: activity.id || newId(),
            name: activity.name || '',
            color: activity.color || ACTIVITY_PALETTE[0],
        })),
        planTypes: (data.planTypes || []).map(plan => ({
            id: plan.id || newId(),
            name: plan.name || '',
            color: plan.color || ACTIVITY_PALETTE[0],
            amount: Number(plan.amount) || 0,
            activities: normalizeRules(plan.activities),
            frequency: normalizeFrequency(plan.frequency),
        })),
    }
    settingsCache[companyId] = { at: Date.now(), data: settings }
    setCurrency(settings.currency)
    return settings
}

export const saveSettings = async (companyId, payload) => {
    await db.collection(SETTINGS_COLLECTION).doc(companyId).set({
        companyId,
        ...payload,
        updatedAt: FieldValue.serverTimestamp(),
    }, { merge: true })
    invalidateSettings(companyId)
    if (payload?.currency) setCurrency(payload.currency)
}

const normalizeMembers = (data) => {
    const members = {}
    Object.entries(data || {}).forEach(([key, value]) => {
        members[key] = { name: value?.name || '' }
    })
    return members
}

let shiftsCache = {}

let bookingsCache = {}

const SHIFTS_TTL = 5 * 60 * 1000

export const invalidateShifts = (companyId = null) => {
    if (!companyId) shiftsCache = {}
    else delete shiftsCache[companyId]
}

export const invalidateBookings = () => {
    bookingsCache = {}
}

export const loadShifts = async (companyId) => {
    const cached = shiftsCache[companyId]
    if (cached && (Date.now() - cached.at) < SHIFTS_TTL) return cached.data

    const snapshot = await db.collection(SHIFTS_COLLECTION).where('companyId', '==', companyId).get()
    const shifts = snapshot.docs.map(doc => {
        const data = doc.data() || {}
        return {
            id: doc.id,
            activityId: data.activityId || null,
            day: Number(data.day) || 1,
            startTime: data.startTime || '08:00',
            endTime: data.endTime || '09:00',
            capacity: Number(data.capacity) || 0,
            members: normalizeMembers(data.members),
        }
    })
    shiftsCache[companyId] = { at: Date.now(), data: shifts }
    return shifts
}

export const saveShift = async (companyId, shift) => {
    const payload = {
        companyId,
        activityId: shift.activityId,
        day: Number(shift.day),
        startTime: shift.startTime,
        endTime: shift.endTime,
        capacity: Number(shift.capacity) || 0,
        updatedAt: FieldValue.serverTimestamp(),
    }

    if (shift.id) {
        await db.collection(SHIFTS_COLLECTION).doc(shift.id).update(payload)
        invalidateShifts(companyId)
        return shift.id
    }

    const doc = await db.collection(SHIFTS_COLLECTION).add({ ...payload, members: {} })
    invalidateShifts(companyId)
    return doc.id
}

export const deleteShift = async (shiftId, companyId = null) => {
    await db.collection(SHIFTS_COLLECTION).doc(shiftId).delete()
    invalidateShifts(companyId)
    invalidateBookings()
}

export const memberKey = (client) => client?.studentId || client?.id || null

export const weekMembers = (shift, bookings = null) => {
    const members = {}
    Object.entries(shift?.members || {}).forEach(([key, member]) => {
        members[key] = { name: member?.name || '', fixed: true }
    })
    Object.entries(bookings?.[shift?.id] || {}).forEach(([key, member]) => {
        if (members[key]) return
        members[key] = { name: member?.name || '', fixed: false }
    })
    return members
}

export const memberCount = (shift, bookings = null) => Object.keys(weekMembers(shift, bookings)).length

export const isShiftFull = (shift, bookings = null) => shift?.capacity > 0
    && memberCount(shift, bookings) >= shift.capacity

export const shiftMembers = (shift, bookings = null) => Object.entries(weekMembers(shift, bookings))
    .map(([id, member]) => ({ id, name: member.name, fixed: member.fixed }))
    .sort((a, b) => a.name.localeCompare(b.name))

export const loadShiftBookings = async (companyId, weekStart) => {
    const key = `${companyId}_${weekStart}`
    const cached = bookingsCache[key]
    if (cached && (Date.now() - cached.at) < SHIFTS_TTL) return cached.data

    const snapshot = await db.collection(BOOKINGS_COLLECTION)
        .where('companyId', '==', companyId)
        .where('weekStart', '==', weekStart)
        .get()

    const bookings = {}
    snapshot.docs.forEach(doc => {
        const data = doc.data() || {}
        if (!data.shiftId) return
        bookings[data.shiftId] = normalizeMembers(data.members)
    })
    bookingsCache[key] = { at: Date.now(), data: bookings }
    return bookings
}

export const loadWeeksBookings = async (companyId, weeks = []) => {
    const result = {}
    const missing = []
    weeks.forEach(weekStart => {
        const cached = bookingsCache[`${companyId}_${weekStart}`]
        if (cached && (Date.now() - cached.at) < SHIFTS_TTL) result[weekStart] = cached.data
        else missing.push(weekStart)
    })
    if (missing.length === 0) return result

    const snapshot = await db.collection(BOOKINGS_COLLECTION)
        .where('companyId', '==', companyId)
        .where('weekStart', 'in', missing)
        .get()

    missing.forEach(weekStart => { result[weekStart] = {} })
    snapshot.docs.forEach(doc => {
        const data = doc.data() || {}
        if (!data.shiftId || !result[data.weekStart]) return
        result[data.weekStart][data.shiftId] = normalizeMembers(data.members)
    })
    missing.forEach(weekStart => {
        bookingsCache[`${companyId}_${weekStart}`] = { at: Date.now(), data: result[weekStart] }
    })
    return result
}

export const removeShiftMember = async (shiftId, key) => {
    await db.collection(SHIFTS_COLLECTION).doc(shiftId).update({
        [`members.${key}`]: FieldValue.delete(),
        updatedAt: FieldValue.serverTimestamp(),
    })
}

export const loadCoachCompanyId = async (coachId) => {
    if (!coachId) return null
    const doc = await db.collection('users').doc(coachId).get()
    const data = doc.data() || {}
    if (data.accountType === 'company') return coachId
    return data.coachSubscription?.companyId || null
}

export const loadClientByEmail = async (companyId, email) => {
    const value = safeLower(email).trim()
    if (!companyId || !value) return null
    const snapshot = await db.collection(CLIENTS_COLLECTION)
        .where('companyId', '==', companyId)
        .where('email', '==', value)
        .limit(1)
        .get()
    if (snapshot.empty) return null
    return normalizeClient(snapshot.docs[0].id, snapshot.docs[0].data())
}

export const SHIFT_FULL = 'shift-full'

export const bookShift = async (shiftId, weekStart, key, name, fixed = false) => {
    const shiftRef = db.collection(SHIFTS_COLLECTION).doc(shiftId)
    const bookingRef = db.collection(BOOKINGS_COLLECTION).doc(bookingDocId(shiftId, weekStart))

    await db.runTransaction(async (transaction) => {
        const shiftDoc = await transaction.get(shiftRef)
        const bookingDoc = await transaction.get(bookingRef)
        if (!shiftDoc.exists) throw new Error(SHIFT_FULL)

        const data = shiftDoc.data() || {}
        const taken = { ...(data.members || {}), ...(bookingDoc.data()?.members || {}) }
        if (taken[key]) return
        const capacity = Number(data.capacity) || 0
        if (capacity > 0 && Object.keys(taken).length >= capacity) throw new Error(SHIFT_FULL)

        if (fixed) {
            transaction.update(shiftRef, {
                [`members.${key}`]: { name: name || '' },
                updatedAt: FieldValue.serverTimestamp(),
            })
            return
        }

        transaction.set(bookingRef, {
            companyId: data.companyId,
            shiftId,
            weekStart,
            members: { [key]: { name: name || '' } },
            updatedAt: FieldValue.serverTimestamp(),
        }, { merge: true })
    })

    invalidateShifts()
    invalidateBookings()
}

export const cancelBooking = async (shiftId, weekStart, key, fixed = false) => {
    if (fixed) {
        await removeShiftMember(shiftId, key)
    } else {
        await db.collection(BOOKINGS_COLLECTION).doc(bookingDocId(shiftId, weekStart)).update({
            [`members.${key}`]: FieldValue.delete(),
            updatedAt: FieldValue.serverTimestamp(),
        })
    }

    invalidateShifts()
    invalidateBookings()
}

export const normalizeRules = (rules = []) => (rules || [])
    .filter(rule => rule?.activityId)
    .map(rule => ({
        activityId: rule.activityId,
        days: (rule.days || []).map(Number).filter(day => WEEK_DAYS.includes(day)),
        startTime: rule.startTime || toTime(DAY_START_HOUR * 60),
        endTime: rule.endTime || toTime(DAY_END_HOUR * 60),
    }))

const ruleCoversShift = (rule, shift) => rule.days.includes(Number(shift?.day))
    && shift?.startTime >= rule.startTime
    && shift?.startTime < rule.endTime

export const planAllowsShift = (shift, rules = [], exceptions = []) => {
    if (rules.length === 0) return { allowed: true, rule: null }

    const planRule = rules.find(rule => rule.activityId === shift?.activityId)
    if (planRule && ruleCoversShift(planRule, shift)) return { allowed: true, rule: planRule }

    const exception = exceptions.find(rule => rule.activityId === shift?.activityId)
    if (exception && ruleCoversShift(exception, shift)) return { allowed: true, rule: exception }

    return { allowed: false, rule: planRule || exception || null }
}

export const FREQUENCY_WEEKLY_MAX = 4

export const FREQUENCY_MONTHLY_MAX = 12

export const FREQUENCY_WEEKLY = 'frequency-weekly'

export const FREQUENCY_MONTHLY = 'frequency-monthly'

const frequencyValue = (value, max) => {
    const number = Number(value)
    return Number.isInteger(number) && number >= 1 && number <= max ? number : null
}

export const normalizeFrequency = (frequency) => ({
    weekly: frequencyValue(frequency?.weekly, FREQUENCY_WEEKLY_MAX),
    monthly: frequencyValue(frequency?.monthly, FREQUENCY_MONTHLY_MAX),
})

export const shiftDate = (weekStart, day) => {
    const date = civilToDate(weekStart)
    if (!date) return ''
    return toDateInput(new Date(date.getFullYear(), date.getMonth(), date.getDate() + (Number(day) || 1) - 1))
}

export const monthWeekStarts = (month) => {
    const [year, index] = String(month).split('-').map(Number)
    if (!year || !index) return []
    const last = toDateInput(new Date(year, index, 0))
    const weeks = []
    let weekStart = weekStartOf(`${month}-01`)
    while (weekStart <= last) {
        weeks.push(weekStart)
        weekStart = addWeeks(weekStart, 1)
    }
    return weeks
}

const weekdayDatesInMonth = (month, day) => monthWeekStarts(month)
    .map(weekStart => shiftDate(weekStart, day))
    .filter(date => monthOf(date) === month)

export const countWeekBookings = (shifts = [], bookings = {}, key) => shifts
    .filter(shift => shift.members?.[key] || bookings?.[shift.id]?.[key])
    .length

export const countMonthBookings = (shifts = [], weeksBookings = {}, key, month) => shifts.reduce((total, shift) => {
    if (shift.members?.[key]) return total + weekdayDatesInMonth(month, shift.day).length
    const single = Object.entries(weeksBookings)
        .filter(([weekStart, bookings]) => bookings?.[shift.id]?.[key] && monthOf(shiftDate(weekStart, shift.day)) === month)
        .length
    return total + single
}, 0)

export const frequencyBlock = ({ shift, weekStart, fixed = false, frequency, shifts = [], bookings = {}, weeksBookings = {}, key }) => {
    const limits = normalizeFrequency(frequency)
    if (!limits.weekly && !limits.monthly) return null

    if (limits.weekly && countWeekBookings(shifts, bookings, key) + 1 > limits.weekly) {
        return { reason: FREQUENCY_WEEKLY, limit: limits.weekly }
    }

    if (limits.monthly) {
        const date = shiftDate(weekStart, shift?.day)
        const month = monthOf(date)
        const weeks = { ...weeksBookings, [weekStart]: bookings }
        const adding = fixed
            ? weekdayDatesInMonth(month, shift?.day).filter(item => item >= date).length
            : 1
        if (countMonthBookings(shifts, weeks, key, month) + adding > limits.monthly) {
            return { reason: FREQUENCY_MONTHLY, limit: limits.monthly }
        }
    }

    return null
}

export const computeShiftsStats = (shifts = [], bookings = null) => {
    const byActivity = {}
    const byTime = {}
    const byDay = {}
    const bySlot = {}
    let taken = 0
    let capacity = 0
    let full = 0

    shifts.forEach(shift => {
        const count = memberCount(shift, bookings)
        taken += count
        capacity += shift.capacity || 0
        if (isShiftFull(shift, bookings)) full += 1

        const activity = byActivity[shift.activityId] || { taken: 0, capacity: 0 }
        byActivity[shift.activityId] = {
            taken: activity.taken + count,
            capacity: activity.capacity + (shift.capacity || 0),
        }

        byTime[shift.startTime] = (byTime[shift.startTime] || 0) + count
        byDay[shift.day] = (byDay[shift.day] || 0) + count

        const slotKey = `${shift.day}|${shift.startTime}`
        const slot = bySlot[slotKey] || { day: shift.day, startTime: shift.startTime, taken: 0, activityIds: [] }
        slot.taken += count
        if (!slot.activityIds.includes(shift.activityId)) slot.activityIds.push(shift.activityId)
        bySlot[slotKey] = slot
    })

    return {
        taken,
        capacity,
        full,
        total: shifts.length,
        occupancy: capacity > 0 ? Math.round(taken / capacity * 100) : 0,
        byActivity,
        byTime,
        byDay,
        topSlots: Object.values(bySlot)
            .filter(slot => slot.taken > 0)
            .sort((a, b) => b.taken - a.taken)
            .slice(0, 3),
    }
}

export const GENDER_OPTIONS = [
    { value: 'M', key: 'gender_male' },
    { value: 'F', key: 'gender_female' },
    { value: 'O', key: 'gender_other' },
]

export const PAYMENT_METHODS = ['transfer', 'cash', 'card', 'other']

export const CLIENT_ORIGINS = ['referral', 'instagram', 'walk_in', 'other']

const CIVIL_DATE = /^\d{4}-\d{2}-\d{2}$/

export const toDateInput = (value) => {
    if (typeof value === 'string') return CIVIL_DATE.test(value) ? value : ''
    const date = toDate(value)
    if (!date) return ''
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${date.getFullYear()}-${month}-${String(date.getDate()).padStart(2, '0')}`
}

export const civilToDate = (value) => {
    const civil = toDateInput(value)
    if (!civil) return null
    const [year, month, day] = civil.split('-').map(Number)
    return new Date(year, month - 1, day)
}

export const today = () => toDateInput(new Date())

export const weekStartOf = (value = new Date()) => {
    const date = civilToDate(value) || toDate(value) || new Date()
    const weekday = date.getDay() || 7
    return toDateInput(new Date(date.getFullYear(), date.getMonth(), date.getDate() - (weekday - 1)))
}

export const addWeeks = (weekStart, weeks = 0) => {
    const date = civilToDate(weekStart)
    if (!date) return weekStartOf()
    return toDateInput(new Date(date.getFullYear(), date.getMonth(), date.getDate() + (weeks * 7)))
}

export const monthsSince = (value, reference = new Date()) => {
    const date = civilToDate(value)
    if (!date) return 0
    let months = (reference.getFullYear() - date.getFullYear()) * 12 + (reference.getMonth() - date.getMonth())
    if (reference.getDate() < date.getDate()) months -= 1
    return Math.max(0, months)
}

export const monthOf = (value) => String(value || '').slice(0, 7)

export const monthIndex = (period) => {
    const year = Number(String(period).slice(0, 4))
    const month = Number(String(period).slice(5, 7))
    if (!year || !month) return null
    return year * 12 + (month - 1)
}

export const periodFromIndex = (index) => {
    const year = Math.floor(index / 12)
    return `${year}-${String((index % 12) + 1).padStart(2, '0')}`
}

export const periodReference = (period) => {
    const year = Number(String(period).slice(0, 4))
    const month = Number(String(period).slice(5, 7))
    const now = new Date()
    if (!year || !month) return now
    const end = new Date(year, month, 0, 23, 59, 59)
    return end > now ? now : end
}

export const leftMonth = (client) => (client?.leftAt ? monthOf(toDateInput(client.leftAt)) : '')

export const isActiveAt = (client, reference) => {
    const joined = civilToDate(client?.joinedAt)
    if (joined && joined > reference) return false
    if (client?.active) return true
    const left = toDate(client?.leftAt)
    return !!left && left > reference
}

export const clientLifespan = (client, reference = new Date()) => {
    if (!client?.joinedAt) return null
    const left = client.active ? null : toDate(client.leftAt)
    return monthsSince(client.joinedAt, left && left < reference ? left : reference)
}

export const average = (values = []) => {
    if (values.length === 0) return null
    return values.reduce((total, value) => total + value, 0) / values.length
}

export const topCounts = (labels = [], topN = 5, otherLabel = '') => {
    const counts = {}
    labels.forEach(label => {
        if (!label) return
        counts[label] = (counts[label] || 0) + 1
    })

    let entries = Object.entries(counts).sort((a, b) => b[1] - a[1])
    if (otherLabel && entries.length > topN) {
        const rest = entries.slice(topN - 1).reduce((total, entry) => total + entry[1], 0)
        entries = [...entries.slice(0, topN - 1), [otherLabel, rest]]
    }
    return entries.map(([label, value]) => ({ label, value }))
}

export const COHORT_MONTHS = 12

export const buildCohorts = (clients = [], period, months = COHORT_MONTHS) => {
    const lastIndex = monthIndex(period)
    if (lastIndex === null) return []
    const reference = periodReference(period)

    const groups = {}
    clients.forEach(client => {
        const index = monthIndex(monthOf(client.joinedAt))
        if (index === null || index > lastIndex) return
        if (!groups[index]) groups[index] = []
        groups[index].push(client)
    })

    const rows = []
    for (let ago = months - 1; ago >= 0; ago -= 1) {
        const index = lastIndex - ago
        const group = groups[index] || []
        const values = []
        for (let mark = 1; mark <= months; mark += 1) {
            if (ago < mark || group.length === 0) {
                values.push(null)
                continue
            }
            const kept = group.filter(client => (clientLifespan(client, reference) || 0) >= mark).length
            values.push(Math.round(kept / group.length * 100))
        }
        rows.push({ period: periodFromIndex(index), total: group.length, values })
    }
    return rows
}

export const daysUntilPayment = (client, reference = new Date()) => {
    const due = civilToDate(client?.nextPayment)
    if (!due) return null
    const from = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate())
    return Math.round((due - from) / 86400000)
}

export const paymentStatus = (client, reference = new Date()) => {
    const days = daysUntilPayment(client, reference)
    if (days === null) return 'none'
    if (days < 0) return 'overdue'
    if (days <= PAYMENT_DUE_DAYS) return 'due'
    return 'ok'
}

const normalizeClient = (id, data = {}) => ({
    id,
    name: data.name || '',
    email: data.email || '',
    planId: data.planId || null,
    planAmount: Number(data.planAmount) || 0,
    method: data.method || '',
    joinedAt: toDateInput(data.joinedAt),
    nextPayment: toDateInput(data.nextPayment),
    active: data.active !== false,
    leftAt: data.leftAt || null,
    origin: data.origin || '',
    sport: data.sport || '',
    area: data.area || '',
    age: Number(data.age) || null,
    job: data.job || '',
    gender: data.gender || '',
    promo: data.promo || null,
    studentId: data.studentId || null,
    exceptions: normalizeRules(data.exceptions),
})

export const loadClients = async (companyId) => {
    const snapshot = await db.collection(CLIENTS_COLLECTION).where('companyId', '==', companyId).get()
    return snapshot.docs.map(doc => normalizeClient(doc.id, doc.data()))
}

export const loadCompanyStudents = async (coachIds = []) => {
    if (coachIds.length === 0) return []
    const byCoach = coachIds.map(id => `studentSubscription.coachId:${id}`).join(' OR ')
    const { hits } = await UserIndex.search('', { filters: `(${byCoach})`, hitsPerPage: 1000 })
    return hits.map(hit => ({
        id: hit.objectID,
        name: hit.name || hit.email || '',
        email: safeLower(hit.email),
        paused: hit.studentSubscription?.paused === 1,
    }))
}

const rosterCache = {}

const ROSTER_TTL = 5 * 60 * 1000

export const invalidateRoster = (companyId) => {
    delete rosterCache[companyId]
}

export const loadRoster = async (companyId) => {
    const cached = rosterCache[companyId]
    if (cached && (Date.now() - cached.at) < ROSTER_TTL) return cached.data

    const coaches = await store.dispatch('fetchCompanyCoaches', companyId)
    const [clients, settings, students] = await Promise.all([
        loadClients(companyId),
        loadSettings(companyId),
        loadCompanyStudents((coaches || []).map(coach => coach.objectID || coach.id)),
    ])

    const data = {
        clients,
        students,
        planTypes: settings.planTypes,
        activities: settings.activities,
        currency: settings.currency,
    }
    rosterCache[companyId] = { at: Date.now(), data }
    return data
}

export const mergeRoster = (clients = [], students = []) => {
    const byStudentId = new Map()
    const byEmail = new Map()
    clients.forEach(client => {
        if (client.studentId) byStudentId.set(client.studentId, client)
        if (client.email) byEmail.set(client.email, client)
    })

    const linked = new Set()
    const rows = students.map(student => {
        const match = byStudentId.get(student.id) || (student.email ? byEmail.get(student.email) : null)
        if (!match) {
            return {
                ...normalizeClient(null, {}),
                studentId: student.id,
                name: student.name,
                email: student.email,
                active: !student.paused,
                fromApp: true,
            }
        }
        linked.add(match.id)
        return { ...match, studentId: student.id, fromApp: true }
    })

    return [...rows, ...clients.filter(client => !linked.has(client.id))]
}

export const emailTaken = async (companyId, email, ignoreId = null) => {
    const value = safeLower(email).trim()
    if (!value) return false
    const snapshot = await db.collection(CLIENTS_COLLECTION)
        .where('companyId', '==', companyId)
        .where('email', '==', value)
        .limit(2)
        .get()
    return snapshot.docs.some(doc => doc.id !== ignoreId)
}

export const saveClient = async (companyId, client) => {
    const payload = {
        companyId,
        name: String(client.name || '').trim(),
        email: safeLower(client.email).trim(),
        planId: client.planId || null,
        planAmount: parseAmount(client.planAmount),
        method: client.method || '',
        joinedAt: toDateInput(client.joinedAt) || null,
        nextPayment: toDateInput(client.nextPayment) || null,
        origin: client.origin || '',
        sport: String(client.sport || '').trim(),
        area: String(client.area || '').trim(),
        age: parseAmount(client.age) || null,
        job: String(client.job || '').trim(),
        gender: client.gender || '',
        promo: client.promo || null,
        exceptions: normalizeRules(client.exceptions),
        updatedAt: FieldValue.serverTimestamp(),
    }

    if (client.studentId) payload.studentId = client.studentId

    if (client.id) {
        await db.collection(CLIENTS_COLLECTION).doc(client.id).update(payload)
        return normalizeClient(client.id, { ...payload, active: client.active !== false })
    }

    const created = {
        ...payload,
        active: client.active !== false,
        studentId: client.studentId || null,
    }
    const doc = await db.collection(CLIENTS_COLLECTION).add({
        ...created,
        createdAt: FieldValue.serverTimestamp(),
    })
    return normalizeClient(doc.id, created)
}

export const setClientActive = async (clientId, active) => {
    await db.collection(CLIENTS_COLLECTION).doc(clientId).update({
        active,
        leftAt: active ? null : FieldValue.serverTimestamp(),
        updatedAt: FieldValue.serverTimestamp(),
    })
}

export const deleteClient = async (clientId) => {
    await db.collection(CLIENTS_COLLECTION).doc(clientId).delete()
}

export const paymentDocId = (companyId, period, clientId) => `${companyId}_${period}_${clientId}`

const normalizePayment = (id, data = {}) => ({
    id,
    clientId: data.clientId || '',
    period: data.period || '',
    amount: Number(data.amount) || 0,
    method: data.method || '',
    date: toDateInput(data.date),
    planId: data.planId || null,
})

const paymentsQuery = (companyId) => db.collection(PAYMENTS_COLLECTION).where('companyId', '==', companyId)

export const loadPeriodPayments = async (companyId, period) => {
    const snapshot = await paymentsQuery(companyId).where('period', '==', period).get()
    return snapshot.docs.map(doc => normalizePayment(doc.id, doc.data()))
}

export const loadYearPayments = async (companyId, year) => {
    const snapshot = await paymentsQuery(companyId)
        .where('period', '>=', `${year}-01`)
        .where('period', '<=', `${year}-12`)
        .get()
    return snapshot.docs.map(doc => normalizePayment(doc.id, doc.data()))
}

export const loadClientPayments = async (companyId, clientId) => {
    const snapshot = await db.collection(PAYMENTS_COLLECTION)
        .where('companyId', '==', companyId)
        .where('clientId', '==', clientId)
        .get()
    return snapshot.docs
        .map(doc => normalizePayment(doc.id, doc.data()))
        .sort((a, b) => b.period.localeCompare(a.period))
}

export const savePayment = async (companyId, payment) => {
    const period = payment.period
    const clientId = payment.clientId
    if (!period || !clientId) return null

    const data = {
        companyId,
        clientId,
        period,
        amount: parseAmount(payment.amount),
        method: payment.method || '',
        date: toDateInput(payment.date) || `${period}-01`,
        planId: payment.planId || null,
        updatedAt: FieldValue.serverTimestamp(),
    }
    await db.collection(PAYMENTS_COLLECTION).doc(paymentDocId(companyId, period, clientId)).set(data, { merge: true })
    return normalizePayment(paymentDocId(companyId, period, clientId), data)
}

export const deletePayment = async (paymentId) => {
    await db.collection(PAYMENTS_COLLECTION).doc(paymentId).delete()
}

export const sumPayments = (payments = []) => payments.reduce((total, p) => total + (Number(p.amount) || 0), 0)

export const nextPaymentAfter = (client, period, paymentDate) => {
    const reference = civilToDate(client?.nextPayment) || civilToDate(paymentDate) || civilToDate(`${period}-01`)
    if (!reference) return null
    const [year, month] = period.split('-').map(Number)
    const day = reference.getDate()
    const target = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0).getDate()
    target.setDate(Math.min(day, lastDay))
    return toDateInput(target)
}

export const setClientNextPayment = async (clientId, nextPayment) => {
    await db.collection(CLIENTS_COLLECTION).doc(clientId).update({
        nextPayment,
        updatedAt: FieldValue.serverTimestamp(),
    })
}

export const saveCash = async (companyId, period, cash) => {
    await db.collection(CASH_COLLECTION).doc(cashDocId(companyId, period)).set({
        companyId,
        period,
        expenses: cash.expenses,
        incomes: cash.incomes,
        funnel: normalizeFunnel(cash.funnel),
        updatedAt: FieldValue.serverTimestamp(),
    }, { merge: true })

    delete yearCache[`${companyId}_${String(period).slice(0, 4)}`]
}
